'use client';

import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { 
  Sparkles, 
  MessageSquare, 
  CheckCheck, 
  Phone, 
  Video, 
  MoreVertical, 
  RotateCcw, 
  Send,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { WhatsAppMessageItem } from './WhatsAppMessagesList';

interface TemplateEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  template: WhatsAppMessageItem | null;
  onSave: (updated: WhatsAppMessageItem) => Promise<void>;
  businessName?: string;
}

const AVAILABLE_TOKENS = [
  { token: '{{cliente}}', label: 'Nombre Cliente' },
  { token: '{{servicio}}', label: 'Servicio' },
  { token: '{{fecha}}', label: 'Fecha' },
  { token: '{{hora}}', label: 'Hora' },
  { token: '{{profesional}}', label: 'Profesional' },
  { token: '{{precio}}', label: 'Precio Total' },
  { token: '{{sena}}', label: 'Monto Seña' },
  { token: '{{negocio}}', label: 'Nombre Negocio' },
  { token: '{{link_gestion}}', label: 'Link Gestión' },
];

export const TemplateEditDrawer: React.FC<TemplateEditDrawerProps> = ({
  isOpen,
  onClose,
  template,
  onSave,
  businessName = 'Juan Rojas Barbería'
}) => {
  const [content, setContent] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testSentMsg, setTestSentMsg] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setContent(template.content || '');
      setEnabled(template.enabled ?? true);
      setSavedSuccess(false);
      setTestSentMsg(null);
    }
  }, [template, isOpen]);

  if (!template) return null;

  const insertToken = (token: string) => {
    setContent((prev) => `${prev} ${token} `);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        ...template,
        content,
        enabled,
      });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Renderizar variables con datos ficticios para la vista previa en vivo
  const renderedPreview = content
    .replace(/\{\{cliente\}\}/g, 'Gonzalo')
    .replace(/\{\{servicio\}\}/g, 'Corte Clásico & Barba')
    .replace(/\{\{fecha\}\}/g, 'Jueves 22 de Octubre')
    .replace(/\{\{hora\}\}/g, '15:30 hs')
    .replace(/\{\{profesional\}\}/g, 'Liliana')
    .replace(/\{\{precio\}\}/g, '$18.000')
    .replace(/\{\{sena\}\}/g, '$5.000')
    .replace(/\{\{negocio\}\}/g, businessName)
    .replace(/\{\{link_gestion\}\}/g, 'https://calenddy.com/r/cmva2795');

  const handleSendTest = async () => {
    if (!testPhone.trim()) {
      setTestSentMsg('Ingresá un número de WhatsApp para probar.');
      return;
    }
    setIsSendingTest(true);
    setTestSentMsg(null);
    try {
      // Mock / Simulación exitosa de despacho
      await new Promise((r) => setTimeout(r, 1000));
      setTestSentMsg(`✓ Mensaje de prueba enviado a +54 ${testPhone}`);
    } catch (e) {
      setTestSentMsg('Error enviando prueba');
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={template.title}
      subtitle={template.subtitle}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Switch de activación */}
          <div className="p-4 bg-slate-50 border border-slate-200/90 rounded-2xl flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-slate-800 block">
                Estado del Disparador
              </span>
              <span className="text-[11px] text-slate-500">
                {enabled ? 'Plantilla activa y lista para disparar' : 'Plantilla desactivada'}
              </span>
            </div>
            <Switch
              checked={enabled}
              onChange={(val) => setEnabled(val)}
              label="Habilitar mensaje automático"
            />
          </div>

          {/* Tokens de Sustitución Dinámica */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Variables Dinámicas Disponibles (Hacé clic para insertar)
            </label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_TOKENS.map((item) => (
                <button
                  key={item.token}
                  type="button"
                  onClick={() => insertToken(item.token)}
                  className="px-2.5 py-1 rounded-lg bg-violet-50 hover:bg-violet-100 text-[#6255F5] border border-violet-200/60 font-mono text-[11px] font-semibold transition cursor-pointer"
                  title={`Insertar ${item.token}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Editor de Texto de la Plantilla */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-slate-700">
                Cuerpo del Mensaje (WhatsApp)
              </label>
              <span className="text-[10px] text-slate-400">
                Soporta formato WhatsApp (*negrita*, _cursiva_)
              </span>
            </div>
            <textarea
              rows={6}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full p-3.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 leading-relaxed font-sans focus:outline-none focus:ring-2 focus:ring-[#6255F5]/20 focus:border-[#6255F5] transition"
              placeholder="Escribí el texto de la notificación..."
            />
          </div>

          {/* Simulador de Pantalla de WhatsApp en Vivo */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-700">
              Vista Previa en Vivo (Así lo recibe el cliente)
            </label>
            <div className="w-full rounded-2xl overflow-hidden shadow-sm border border-slate-300 flex flex-col bg-[#efeae2] relative min-h-[220px]">
              {/* Cabecera Verde de WhatsApp */}
              <div className="bg-[#075e54] text-white px-3.5 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-emerald-700 text-white font-bold text-[10px] flex items-center justify-center border border-white/30">
                    JR
                  </div>
                  <div>
                    <h4 className="text-[11px] font-bold leading-tight">{businessName}</h4>
                    <p className="text-[9px] text-emerald-200 leading-none">en línea</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Video className="w-3 h-3" />
                  <Phone className="w-3 h-3" />
                  <MoreVertical className="w-3 h-3" />
                </div>
              </div>

              {/* Burbuja de Chat */}
              <div className="p-3.5 flex-1 flex flex-col justify-end space-y-2">
                <div className="self-center bg-white/80 text-slate-600 px-2 py-0.5 rounded-md text-[9px] font-semibold shadow-2xs">
                  HOY
                </div>
                <div className="self-start max-w-[90%] bg-white rounded-2xl rounded-tl-xs p-3 shadow-xs space-y-1 relative border border-slate-200/60">
                  <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-line">
                    {renderedPreview}
                  </p>
                  <div className="flex items-center justify-end gap-1 pt-0.5 text-[9px] text-slate-400">
                    <span>14:02</span>
                    <CheckCheck className="w-3 h-3 text-sky-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enviar Prueba a Celular */}
          <div className="p-4 bg-slate-50 border border-slate-200/90 rounded-2xl space-y-2.5">
            <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5 text-slate-500" />
              <span>Probar Envío a mi Teléfono</span>
            </span>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="ej: 11 3176 7545"
                className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6255F5]/20 focus:border-[#6255F5]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTest}
                isLoading={isSendingTest}
              >
                Probar
              </Button>
            </div>
            {testSentMsg && (
              <p className="text-[11px] font-bold text-emerald-600">
                {testSentMsg}
              </p>
            )}
          </div>
        </div>

        {/* Footer Sticky */}
        <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-between shrink-0">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <div className="flex items-center gap-2">
            {savedSuccess && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                ¡Guardado!
              </span>
            )}
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              Guardar Plantilla
            </Button>
          </div>
        </div>
      </div>
    </SlideOverDrawer>
  );
};
